/**
 * Client-safe port of `routeForScope` from `excellent/src/departments.ts`.
 *
 * The departments module imports `@excellent/core` (the barrel), which pulls
 * node:fs / better-sqlite3 into the client dep-scan and fails the browser-only
 * guard. This file re-derives the same pure helpers from the client-safe
 * `@excellent/core/section-registry` subpath — same SECTION_REGISTRY, same
 * rewrite rules — without the server barrel.
 */

import { SECTION_REGISTRY } from "@excellent/core/section-registry";

export type ScopeTarget = { kind: "all" } | { kind: "project"; slug: string };

type Department = {
  slug: string;
  allHref: string;
  scopedHref: (projectSlug: string) => string;
};

const CORE: Department[] = [
  {
    slug: "home",
    allHref: "/",
    scopedHref: (s) => `/projects/${s}`,
  },
  {
    slug: "issues",
    allHref: "/issues",
    scopedHref: (s) => `/projects/${s}/issues`,
  },
  {
    slug: "planner",
    allHref: "/planner",
    scopedHref: (s) => `/projects/${s}/planner`,
  },
  {
    slug: "agents",
    allHref: "/",
    scopedHref: (s) => `/projects/${s}/agents`,
  },
];

const SECTIONS: Department[] = SECTION_REGISTRY.map((e) => ({
  slug: e.slug,
  allHref: `/projects/sections/${e.slug}`,
  scopedHref: (s: string) => `/projects/${s}/${e.slug}/overview`,
}));

const DEPARTMENTS: Department[] = [...CORE, ...SECTIONS];
const BY_SLUG = new Map(DEPARTMENTS.map((d) => [d.slug, d]));
const FOLDABLE = new Set(SECTIONS.map((d) => d.slug));
const NON_PROJECT = new Set(["sections", "map", "new"]);

function departmentBySlug(slug: string): Department | null {
  return BY_SLUG.get(slug) ?? null;
}

function departmentForPath(pathname: string): Department | null {
  if (
    pathname === "/verification" ||
    pathname.startsWith("/verification/") ||
    pathname === "/agents/verification" ||
    pathname.startsWith("/agents/verification/") ||
    pathname === "/agents/verification-ops"
  ) {
    return {
      slug: "verification",
      allHref: pathname,
      scopedHref: () => pathname,
    };
  }
  const scoped = pathname.match(/^\/projects\/([^/?#]+)(?:\/([^/?#]+))?/);
  const projectSeg = scoped?.[1];
  if (projectSeg && !NON_PROJECT.has(projectSeg)) {
    const sub = scoped?.[2];
    if (!sub) return departmentBySlug("home");
    if (sub === "issues") return departmentBySlug("issues");
    if (sub === "planner") return departmentBySlug("planner");
    if (sub === "agents") return departmentBySlug("agents");
    if (FOLDABLE.has(sub)) return departmentBySlug(sub);
    return null;
  }
  if (pathname === "/") return departmentBySlug("home");
  for (const slug of ["issues", "planner", "agents"]) {
    if (pathname === `/${slug}` || pathname.startsWith(`/${slug}/`)) {
      return departmentBySlug(slug);
    }
  }
  const allSection = pathname.match(/^\/projects\/sections\/([^/?#]+)/);
  const sectionSeg = allSection?.[1];
  if (sectionSeg && FOLDABLE.has(sectionSeg)) {
    return departmentBySlug(sectionSeg);
  }
  return null;
}

/**
 * Map the CURRENT pathname to the equivalent route in the target scope,
 * preserving department + query string. Faithful port of
 * `excellent/src/departments.ts` `routeForScope`.
 */
export function routeForScope(
  pathname: string,
  search: string,
  target: ScopeTarget,
): string {
  const dep = departmentForPath(pathname);
  if (!dep) {
    return target.kind === "all" ? "/" : `/projects/${target.slug}`;
  }
  const base =
    target.kind === "all" ? dep.allHref : dep.scopedHref(target.slug);
  return base + (search || "");
}
